"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { track } from "@vercel/analytics";

import {
  BOOKED_PATH,
  BOOKED_STORAGE_KEY,
  BOOKING_MINUTES,
  CALENDLY_URL,
  bookingReferenceFrom,
  calendlyEmbedUrl,
  calendlyExternalUrl,
  calendlyEventName,
} from "@/lib/booking";
import { ContactButtons, ContactTextLink } from "@/components/ownly/ContactActions";
import { OwnBookingStep } from "@/components/ownly/OwnBookingStep";
import { PreCallForm } from "@/components/ownly/PreCallForm";
import { adTagsLoaded, bookingEventId, trackBookingConversion } from "@/lib/ad-tags";
import { BOOKING_SLUG } from "@/lib/own-booking";
import { preCallCalendlyNote, type PreCallAnswers } from "@/lib/precall";
import {
  currentAdClickIds,
  currentCampaign,
  readAdClickIds,
  readCampaignTag,
} from "@/lib/campaign-source";

/**
 * Three quick questions and a name and email first, then pick a time.
 *
 * WHAT CHANGED, 2026-09-02. This page used to be a button that loaded a calendar. Anyone could
 * take half an hour out of the week and arrive as a name and an email address, so the call began
 * with the questions a form answers in twenty seconds and the research nobody had been able to do.
 * Tom: *"it's a bit weird. It's not really what you would do if you were about to jump on a
 * professional call with someone."*
 *
 * PECR, UNCHANGED AND STILL THE REASON THE FRAME IS LAZY. Calendly is a third party that sets its
 * own cookies inside its frame. The site's cookie banner (CookieConsent, added 2026-09-30) only
 * covers the ad tags, so Calendly keeps its own consent route: the frame still loads only after a
 * deliberate press, on a button that says what pressing it
 * does, with the cookie sentence directly under it. "Show available times" is that deliberate
 * press, so the visitor knows exactly when the third-party calendar will load.
 *
 * The direct Calendly link stays visible throughout, so a blocked frame, a locked-down browser or
 * a screen reader never leaves someone with no way to book. Skipping the questions that way is
 * fine: a booking with no brief still beats no booking.
 */

/* Stated up front because these are the things people want to know before they hand over a slot
   in their day, and none of them are on the Calendly page. */
const CALL_FACTS = [
  { term: "Length", detail: `${BOOKING_MINUTES} minutes` },
  { term: "With", detail: "An Ownly consultant" },
  { term: "Cost", detail: "Nothing, and no obligation" },
  { term: "Before", detail: "Three quick questions and your name, about a minute" },
];

export function BookingFlow({
  ownBooking = false,
  contactEmail = "team@ownlydigital.co.uk",
  host = null,
  hostName = null,
}: {
  /** Ownly's own date and time picker instead of the Calendly frame. OWN_BOOKING_ENABLED, read by /book. */
  ownBooking?: boolean;
  contactEmail?: string;
  /** A person's own page (/book/tom). Absent = the shared /book, shared out between the team. */
  host?: string | null;
  /** That person's name, for the "not taking bookings yet" message. */
  hostName?: string | null;
} = {}) {
  const params = useSearchParams();
  /* ?type=intro for a meeting type other than the 30-minute review. Checked, never trusted. */
  const rawType = params.get("type");
  const type = rawType && BOOKING_SLUG.test(rawType) ? rawType : null;
  const router = useRouter();
  const [answers, setAnswers] = useState<PreCallAnswers | null>(null);
  const [scheduled, setScheduled] = useState(false);
  const selectedTimeTrackedRef = useRef(false);
  const completedTrackedRef = useRef(false);

  /* Someone arriving from one of our emails already has their details in the link, so the calendar
     is prefilled from those as well as from the form. */
  const linkName = params.get("name");
  const linkEmail = params.get("email");
  const linkPhone = params.get("phone");
  const linkNote = params.get("note");
  const campaign = readCampaignTag(new URLSearchParams(params.toString())) ?? currentCampaign();
  const urlClickIds = readAdClickIds(new URLSearchParams(params.toString()));
  const rememberedClickIds = currentAdClickIds();
  const clickIds = {
    gclid: urlClickIds.gclid ?? rememberedClickIds.gclid,
    msclkid: urlClickIds.msclkid ?? rememberedClickIds.msclkid,
    fbclid: urlClickIds.fbclid ?? rememberedClickIds.fbclid,
  };

  const src = calendlyEmbedUrl({
    name: answers?.name ?? linkName,
    email: answers?.email ?? linkEmail,
    /* The mobile from the form, or the one an email link already carries. */
    phone: answers?.phone || linkPhone,
    note: answers ? preCallCalendlyNote(answers) : linkNote,
    utmSource: campaign?.source,
    utmMedium: campaign?.medium,
    utmCampaign: campaign?.campaign,
    /* The same answers again, hidden, because the notes box above can be typed over. */
    answers: answers ? preCallCalendlyNote(answers) : null,
    ...clickIds,
    /* adTagsLoaded() is only true after an Accept on this page or a remembered one. Read at
       render, which for the frame is after the form, so a yes given on the way here counts. */
    adsConsent: typeof window !== "undefined" && adTagsLoaded(),
    /* Only read once the frame is on screen, which is after a press, so the server render never
       needs it. See `embedDomain` in lib/booking.ts for why the frame is silent without it. */
    embedDomain: typeof window === "undefined" ? null : window.location.hostname,
  });
  const externalUrl = calendlyExternalUrl({
    name: linkName,
    email: linkEmail,
    phone: linkPhone,
    note: linkNote,
    utmSource: campaign?.source,
    utmMedium: campaign?.medium,
    utmCampaign: campaign?.campaign,
    ...clickIds,
    adsConsent: typeof window !== "undefined" && adTagsLoaded(),
  });

  useEffect(() => {
    if (!answers || ownBooking) return;
    const calendlyOrigin = new URL(CALENDLY_URL).origin;
    const onMessage = (message: MessageEvent) => {
      if (message.origin !== calendlyOrigin) return;
      const event = calendlyEventName(message.data);
      if (event === "calendly.date_and_time_selected" && !selectedTimeTrackedRef.current) {
        selectedTimeTrackedRef.current = true;
        track("booking-time-selected", { source: campaign?.source ?? "direct" });
      }
      if (event === "calendly.event_scheduled" && !completedTrackedRef.current) {
        completedTrackedRef.current = true;
        setScheduled(true);
        /* Two opaque references, so /booked can show the time and host from Calendly itself.
           Session storage, never the address bar: see BOOKED_STORAGE_KEY. */
        const reference = bookingReferenceFrom(message.data);
        if (reference) {
          try {
            window.sessionStorage.setItem(BOOKED_STORAGE_KEY, JSON.stringify(reference));
          } catch {
            /* Private mode or storage switched off: /booked falls back to its general version. */
          }
        }
        track("booking-completed", { source: campaign?.source ?? "direct" });
        /* The ad tags, if the visitor accepted them (trackBookingConversion does nothing
           otherwise). The id is built from the invitee uuid so Lead Engine's server copy of the
           same booking, sent from the Calendly webhook, is recognised as a duplicate by Meta. */
        if (reference?.invitee) trackBookingConversion(bookingEventId(reference.invitee));
        /* On to the page that says what happens next. The source goes with it only as far as
           the analytics event above; nothing about the booking is put in the address bar. */
        router.push(BOOKED_PATH);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [answers, ownBooking, campaign?.source, router]);

  if (answers && ownBooking) {
    /* The backup, shown only when our booking cannot work (no calendar connected, Lead Engine
       down), so nobody is ever left without a way to reach us: WhatsApp, a call or our inbox.
       It was Calendly's own page until Calendly was retired on 2026-10-11; a link to a calendar
       that is being cancelled is not a backup. */
    return (
      <OwnBookingStep
        answers={answers}
        host={host}
        hostName={hostName}
        type={type}
        campaign={campaign}
        contactEmail={contactEmail}
        fallback={
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <ContactButtons placement="book" />
            <a
              href={`mailto:${contactEmail}`}
              className="text-copy font-medium text-ownly-accent-text underline decoration-ownly-accent/35 underline-offset-2 transition-colors hover:text-[#5ca3ff]"
            >
              Email {contactEmail}
            </a>
          </div>
        }
      />
    );
  }

  if (answers) {
    return (
      <div className="mt-8">
        <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <p className="ownly-body leading-[23px] text-white/85">
            {scheduled ? "Your call is booked." : "Pick a time that suits you."}
          </p>
          <p className="text-copy text-white/55">
            {scheduled
              ? "We will email your confirmation. The joining link is in your calendar invite."
              : "Your name and email are already filled in."}
          </p>
        </div>
        <div className="overflow-hidden rounded-2xl border border-white/[0.12] bg-[#0b1120] shadow-[var(--ownly-shadow-card)]">
          <iframe
            src={src}
            title={`Choose a time for a ${BOOKING_MINUTES}-minute review with Ownly`}
            className="block h-[52rem] w-full border-0 sm:h-[46rem]"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <PreCallForm
        ownBooking={ownBooking}
        onReady={(given) => {
          track("booking-calendar-opened", { source: campaign?.source ?? "direct" });
          setAnswers(given);
        }}
      />

      <div className="mt-7 grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-start">
        {ownBooking ? (
          <p className="text-copy leading-[24px] text-white/55">
            Form will not load?{" "}
            <ContactTextLink
              method="whatsapp"
              placement="book"
              className="font-medium text-ownly-accent-text underline decoration-ownly-accent/35 underline-offset-2 transition-colors hover:text-[#5ca3ff]"
            >
              WhatsApp us
            </ContactTextLink>
            ,{" "}
            <ContactTextLink
              method="call"
              placement="book"
              className="font-medium text-ownly-accent-text underline decoration-ownly-accent/35 underline-offset-2 transition-colors hover:text-[#5ca3ff]"
            >
              give us a ring
            </ContactTextLink>{" "}
            or email{" "}
            <a
              href={`mailto:${contactEmail}`}
              className="font-medium text-ownly-accent-text underline decoration-ownly-accent/35 underline-offset-2 transition-colors hover:text-[#5ca3ff]"
            >
              {contactEmail}
            </a>{" "}
            and we will find a time.
          </p>
        ) : (
          <p className="text-copy leading-[24px] text-white/55">
            Form will not load? Book straight from{" "}
            <a
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("booking-calendar-external", { source: campaign?.source ?? "direct" })}
              className="font-medium text-ownly-accent-text underline decoration-ownly-accent/35 underline-offset-2 transition-colors hover:text-[#5ca3ff]"
            >
              our booking page
            </a>
            . We will just have less to go on.
          </p>
        )}

        {/* Real facts in a real definition list, not a decorative terminal. */}
        <dl className="hidden lg:block">
          {CALL_FACTS.map((fact) => (
            <div
              key={fact.term}
              className="flex items-baseline justify-between gap-6 border-t border-white/[0.1] py-3 first:border-t-0 first:pt-0"
            >
              <dt className="shrink-0 ownly-label text-white/55">
                {fact.term}
              </dt>
              <dd className="text-right text-copy leading-[21px] text-white/85">{fact.detail}</dd>
            </div>
          ))}
        </dl>
      </div>
    </>
  );
}
